import { type QueryKey, type UseQueryOptions } from '@tanstack/react-query'
import {
  type CollectionSchema,
  type MetaOutputOptions,
  type SchemaFirestoreQueryFactory,
  type SchemaQuerySpecification,
} from 'zod-firebase'

/**
 * Query state type that matches the snapshot query options return type.
 * This ensures compatibility with existing code that expects { data: T[] } structure.
 */
export interface QueryReadState<T> {
  data: T[]
}

/**
 * Options for a one-time read query (non-snapshot).
 * Mirrors the schemaQuerySnapshotQueryOptions interface but uses getDocs instead of onSnapshot.
 */
interface SchemaQueryReadQueryOptions<
  TCollectionSchema extends CollectionSchema,
  TOptions extends MetaOutputOptions = MetaOutputOptions,
  TQueryKey extends QueryKey = QueryKey,
> {
  queryKey: TQueryKey
  factory: SchemaFirestoreQueryFactory<TCollectionSchema>
  query?: SchemaQuerySpecification<TCollectionSchema, TOptions> | null
  enabled?: boolean
  staleTime?: number
  gcTime?: number
}

type SchemaDocumentOutput<
  TCollectionSchema extends CollectionSchema,
  TOptions extends MetaOutputOptions,
> = TCollectionSchema extends { zod: infer Z }
  ? Z extends { _output: infer O }
    ? O & (TOptions extends { _id: false } ? object : { readonly _id: string })
    : never
  : never

/**
 * Creates TanStack Query options for a one-time Firestore read (getDocs).
 * Use this for closed evenings where data won't change, avoiding unnecessary snapshot listeners.
 *
 * The returned data structure matches schemaQuerySnapshotQueryOptions for compatibility:
 * { data: T[] } where T is the document type.
 *
 * @example
 * ```ts
 * // For closed evenings, use read query (one-time fetch)
 * const query = schemaQueryReadQueryOptions({
 *   queryKey: ['stats', 'transportRequests', eveningId],
 *   factory: collections.transportRequests,
 *   query: { where: [['eveningId', '==', eveningId]] },
 *   staleTime: Infinity, // Data won't change
 * })
 * ```
 */
export const schemaQueryReadQueryOptions = <
  TCollectionSchema extends CollectionSchema,
  TOptions extends MetaOutputOptions = MetaOutputOptions,
  TQueryKey extends QueryKey = QueryKey,
>({
  queryKey,
  factory,
  query,
  enabled = true,
  staleTime = Infinity,
  gcTime,
}: SchemaQueryReadQueryOptions<TCollectionSchema, TOptions, TQueryKey>): UseQueryOptions<
  QueryReadState<SchemaDocumentOutput<TCollectionSchema, TOptions>>,
  Error,
  QueryReadState<SchemaDocumentOutput<TCollectionSchema, TOptions>>,
  TQueryKey
> => ({
  queryKey,
  queryFn: async () => {
    if (!query) {
      return { data: [] }
    }
    const data = await factory.findMany(query)
    return { data } as QueryReadState<SchemaDocumentOutput<TCollectionSchema, TOptions>>
  },
  enabled: enabled && query != null,
  staleTime,
  gcTime,
})
